import {
  pgTable,
  text,
  timestamp,
  uuid,
  boolean,
  integer,
  index,
  check,
  jsonb,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";

const tz = { withTimezone: true } as const;

// ---------------------------------------------------------------------------
// Webinar Chat Messages — Durable store for realtime chat
// ---------------------------------------------------------------------------

export const webinarChatMessages = pgTable(
  "webinar_chat_messages",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    webinarId: uuid("webinar_id").notNull(),             // FK to webinars
    senderId: uuid("sender_id"),                          // user ID (null for system messages)
    senderName: text("sender_name").notNull(),            // display name at send time
    senderRole: text("sender_role").notNull().default("attendee"),
      // "host" | "co_host" | "moderator" | "attendee" | "system"
    messageType: text("message_type").notNull().default("text"),
      // "text" | "question" | "announcement" | "system" | "reaction"
    content: text("content").notNull(),
    // Moderation
    isModerated: boolean("is_moderated").notNull().default(false),
    moderatedBy: uuid("moderated_by"),
    moderatedAt: timestamp("moderated_at", tz),
    moderationReason: text("moderation_reason"),
    // Threading (for Q&A)
    parentMessageId: uuid("parent_message_id"),           // reply-to (self-referencing)
    // Metadata
    metadata: jsonb("metadata"),                          // reactions count, pinned, highlighted
    isDeleted: boolean("is_deleted").notNull().default(false),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_webinar_chat_webinar").on(table.webinarId, table.createdAt),
    index("idx_webinar_chat_tenant").on(table.tenantId),
    index("idx_webinar_chat_sender").on(table.senderId),
    index("idx_webinar_chat_parent").on(table.parentMessageId),
    check(
      "chk_webinar_chat_role",
      sql`${table.senderRole} IN ('host', 'co_host', 'moderator', 'attendee', 'system')`,
    ),
    check(
      "chk_webinar_chat_type",
      sql`${table.messageType} IN ('text', 'question', 'announcement', 'system', 'reaction')`,
    ),
  ],
);

// ---------------------------------------------------------------------------
// Webinar Polls — Structured polls with response tracking
// ---------------------------------------------------------------------------

export const webinarPolls = pgTable(
  "webinar_polls",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    webinarId: uuid("webinar_id").notNull(),             // FK to webinars
    question: text("question").notNull(),
    pollType: text("poll_type").notNull().default("single_choice"),
      // "single_choice" | "multiple_choice" | "word_cloud" | "rating" | "open_text"
    options: jsonb("options"),
      // For choice types: [{ id: "opt_1", text: "Option A" }, ...]
      // For rating: { min: 1, max: 5, labels: { 1: "Poor", 5: "Excellent" } }
      // For open_text/word_cloud: null
    isAnonymous: boolean("is_anonymous").notNull().default(false),
    showResultsToAttendees: boolean("show_results_to_attendees").notNull().default(true),
    // Lifecycle
    status: text("status").notNull().default("draft"),
      // "draft" | "active" | "closed" | "archived"
    launchedAt: timestamp("launched_at", tz),
    closedAt: timestamp("closed_at", tz),
    launchedBy: uuid("launched_by"),                     // host who launched
    sortOrder: integer("sort_order").notNull().default(0),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_webinar_polls_webinar").on(table.webinarId),
    index("idx_webinar_polls_tenant").on(table.tenantId),
    index("idx_webinar_polls_status").on(table.status),
    check(
      "chk_webinar_poll_type",
      sql`${table.pollType} IN ('single_choice', 'multiple_choice', 'word_cloud', 'rating', 'open_text')`,
    ),
    check(
      "chk_webinar_poll_status",
      sql`${table.status} IN ('draft', 'active', 'closed', 'archived')`,
    ),
  ],
);

// ---------------------------------------------------------------------------
// Webinar Poll Responses — Individual attendee responses
// ---------------------------------------------------------------------------

export const webinarPollResponses = pgTable(
  "webinar_poll_responses",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    pollId: uuid("poll_id").notNull(),                   // FK to webinar_polls
    webinarId: uuid("webinar_id").notNull(),             // denormalized for query speed
    respondentId: uuid("respondent_id"),                  // user ID (null if anonymous + no auth)
    // Response data (flexible per poll type)
    selectedOptionIds: text("selected_option_ids").array(), // for choice types
    ratingValue: integer("rating_value"),                    // for rating type
    textResponse: text("text_response"),                     // for open_text / word_cloud
    // Metadata
    respondedAt: timestamp("responded_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_poll_responses_poll").on(table.pollId),
    index("idx_poll_responses_webinar").on(table.webinarId),
    index("idx_poll_responses_tenant").on(table.tenantId),
    index("idx_poll_responses_respondent").on(table.respondentId),
  ],
);

// ---------------------------------------------------------------------------
// Webinar Surveys — Multi-question survey definitions
// ---------------------------------------------------------------------------

export const webinarSurveys = pgTable(
  "webinar_surveys",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    webinarId: uuid("webinar_id").notNull(),
    title: text("title").notNull(),
    surveyType: text("survey_type").notNull().default("multi_question"),
    questions: jsonb("questions").notNull(),
    displayAt: text("display_at").notNull().default("post"),
    status: text("status").notNull().default("draft"),
    sortOrder: integer("sort_order").notNull().default(0),
    createdBy: uuid("created_by"),
    launchedAt: timestamp("launched_at", tz),
    closedAt: timestamp("closed_at", tz),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_webinar_surveys_webinar").on(table.webinarId),
    index("idx_webinar_surveys_tenant").on(table.tenantId),
    index("idx_webinar_surveys_status").on(table.status),
    check(
      "chk_webinar_survey_type",
      sql`${table.surveyType} IN ('quick_poll', 'nps', 'multi_question', 'rating_matrix', 'open_ended', 'prioritization')`,
    ),
    check(
      "chk_webinar_survey_display_at",
      sql`${table.displayAt} IN ('pre', 'during', 'post')`,
    ),
    check(
      "chk_webinar_survey_status",
      sql`${table.status} IN ('draft', 'active', 'closed', 'archived')`,
    ),
  ],
);

// ---------------------------------------------------------------------------
// Webinar Survey Responses — Individual attendee survey answers
// ---------------------------------------------------------------------------

export const webinarSurveyResponses = pgTable(
  "webinar_survey_responses",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    webinarId: uuid("webinar_id").notNull(),
    surveyId: uuid("survey_id").notNull(),
    respondentId: uuid("respondent_id"),
    answers: jsonb("answers").notNull(),
    submittedAt: timestamp("submitted_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_webinar_survey_responses_survey").on(table.surveyId),
    index("idx_webinar_survey_responses_webinar").on(table.webinarId),
    index("idx_webinar_survey_responses_tenant").on(table.tenantId),
    index("idx_webinar_survey_responses_respondent").on(table.respondentId),
  ],
);
